import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@app/store/store';
import { IAdminUsersFilterRequest } from '@app/interfaces/adminUser';

export interface AdminUsersFilterState {
  // Filter options loaded from API
  filterOptions: {
    roles: string[];
    salesPoints: { id: number | string; name?: string }[];
    organizationalEntities: { id: number; name?: string }[];
  };

  // Applied filters
  search: string;
  firstname: string;
  lastname: string;
  selectedRoles: string[];
  selectedSalesPointIds: Array<number | string>;
  selectedOrgEntityIds: number[];
  showSoftDeleted: boolean;

  // Temp UI state
  firstnameTemp: string;
  lastnameTemp: string;
  selectedRolesTemp: string[];
  selectedSalesPointIdsTemp: Array<number | string>;
  selectedOrgEntityIdsTemp: number[];

  // Control flags
  shouldTriggerFetch: boolean;
}

const initialState: AdminUsersFilterState = {
  filterOptions: {
    roles: [],
    salesPoints: [],
    organizationalEntities: [],
  },

  search: '',
  firstname: '',
  lastname: '',
  selectedRoles: [],
  selectedSalesPointIds: [],
  selectedOrgEntityIds: [],
  showSoftDeleted: false,

  firstnameTemp: '',
  lastnameTemp: '',
  selectedRolesTemp: [],
  selectedSalesPointIdsTemp: [],
  selectedOrgEntityIdsTemp: [],

  shouldTriggerFetch: false,
};

const adminUsersFiltersSlice = createSlice({
  name: 'adminUsersFilters',
  initialState,
  reducers: {
    // Set filter options from API
    setFilterOptions(state, action: PayloadAction<AdminUsersFilterState['filterOptions']>) {
      state.filterOptions = action.payload;
    },

    // Applied filters
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setFirstname(state, action: PayloadAction<string>) {
      state.firstname = action.payload;
    },
    setLastname(state, action: PayloadAction<string>) {
      state.lastname = action.payload;
    },
    setSelectedRoles(state, action: PayloadAction<string[]>) {
      state.selectedRoles = action.payload;
    },
    setSelectedSalesPointIds(state, action: PayloadAction<Array<number | string>>) {
      state.selectedSalesPointIds = action.payload;
    },
    setSelectedOrgEntityIds(state, action: PayloadAction<number[]>) {
      state.selectedOrgEntityIds = action.payload;
    },
    setShowSoftDeleted(state, action: PayloadAction<boolean>) {
      state.showSoftDeleted = action.payload;
    },

    // Temp (UI) filters
    setFirstnameTemp(state, action: PayloadAction<string>) {
      state.firstnameTemp = action.payload;
    },
    setLastnameTemp(state, action: PayloadAction<string>) {
      state.lastnameTemp = action.payload;
    },
    setSelectedRolesTemp(state, action: PayloadAction<string[]>) {
      state.selectedRolesTemp = action.payload;
    },
    setSelectedSalesPointIdsTemp(state, action: PayloadAction<Array<number | string>>) {
      state.selectedSalesPointIdsTemp = action.payload;
    },
    setSelectedOrgEntityIdsTemp(state, action: PayloadAction<number[]>) {
      state.selectedOrgEntityIdsTemp = action.payload;
    },

    // Control
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },

    resetFilters(state) {
      state.search = '';
      state.firstname = '';
      state.lastname = '';
      state.selectedRoles = [];
      state.selectedSalesPointIds = [];
      state.selectedOrgEntityIds = [];
      state.showSoftDeleted = false;

      state.firstnameTemp = '';
      state.lastnameTemp = '';
      state.selectedRolesTemp = [];
      state.selectedSalesPointIdsTemp = [];
      state.selectedOrgEntityIdsTemp = [];

      state.shouldTriggerFetch = true;
    },
  },
});

export const adminUsersFiltersActions = adminUsersFiltersSlice.actions;
export default adminUsersFiltersSlice.reducer;

// --- Selectors ---
export const areFiltersAppliedSelector = (state: RootState): boolean => {
  const s = state.adminUsersFilters;
  return (
    !!s.search ||
    !!s.firstname ||
    !!s.lastname ||
    s.selectedRoles.length > 0 ||
    s.selectedSalesPointIds.length > 0 ||
    s.selectedOrgEntityIds.length > 0 ||
    s.showSoftDeleted
  );
};

export const activeFiltersCountSelector = (state: RootState): number => {
  const s = state.adminUsersFilters;
  let count = 0;
  if (s.search) count++;
  if (s.firstname) count++;
  if (s.lastname) count++;
  if (s.selectedRoles.length > 0) count++;
  if (s.selectedSalesPointIds.length > 0) count++;
  if (s.selectedOrgEntityIds.length > 0) count++;
  if (s.showSoftDeleted) count++;

  return count;
};

export const buildAdminUsersFilterSelector = (state: RootState): IAdminUsersFilterRequest => {
  const s = state.adminUsersFilters;
  return buildAdminUsersFilter({
    search: s.search,
    firstname: s.firstname,
    lastname: s.lastname,
    roles: s.selectedRoles,
    salesPointIds: s.selectedSalesPointIds,
    orgEntityIds: s.selectedOrgEntityIds,
    showSoftDeleted: s.showSoftDeleted,
  });
};

export const buildAdminUsersFilter = ({
  search,
  firstname,
  lastname,
  roles,
  salesPointIds,
  orgEntityIds,
  showSoftDeleted,
}: {
  search?: string;
  firstname?: string;
  lastname?: string;
  roles?: string[]; // or single
  salesPointIds?: Array<number | string>;
  orgEntityIds?: number[];
  showSoftDeleted?: boolean;
}): IAdminUsersFilterRequest => {
  const filter: IAdminUsersFilterRequest = {};

  if (typeof showSoftDeleted === 'boolean') filter.showSoftDeleted = showSoftDeleted;
  if (search) filter.search = search;
  if (firstname) filter.firstname = firstname;
  if (lastname) filter.lastname = lastname;

  if (roles?.length === 1) filter.role = roles[0];
  else if (roles && roles.length > 1) filter.role = roles;

  if (salesPointIds?.length) {
    // ensure numeric for API
    filter.salespointId = salesPointIds.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  }

  if (orgEntityIds?.length) {
    filter.organizationalEntityId = orgEntityIds;
  }

  return filter;
};
